'use client';

import { ProductImages } from '@eliadmin/core/src/components/product/detail/product-images';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { VariantsParamsSync, VariantsProvider } from '@eliadmin/core/src/stores/variant-store';
import { Suspense } from 'react';
import { DialogProvider } from '../ui/dialog';
import { WeekProductInfo } from '../product/week-product-info';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import type { DailyDeal } from '@eliadmin/core/src/api/daily-deals/daily-deals-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchDailyDeals } from '@eliadmin/core/src/api/daily-deals/daily-deals-api';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useQuery } from '@tanstack/react-query';

export function WeekProduct({
  data,
  locale,
  className,
  managedExternally = false,
}: {
  data: DailyDeal;
  locale: string;
  className?: string;
  managedExternally?: boolean;
}) {
  const initialProduct = data.items[0].product;
  const { me, isInitialized } = useCustomerStore();
  const { data: queryProduct = initialProduct } = useQuery({
    queryKey: [
      CacheKey.DAILY_DEALS,
      'week-product',
      locale,
      initialProduct.slug,
      me?.customer.id ?? 'guest',
    ],
    queryFn: () =>
      fetchDailyDeals({
        locale,
        authToken: me?.authToken,
        query: { page: 1, results: 2 },
        where: { is_running: { equals: true } },
      })
        .then(
          response =>
            response.data.find(deal => deal.id === data.id)?.items[0]?.product ?? initialProduct,
        )
        .catch(() => initialProduct),
    enabled: !managedExternally && isInitialized && !!initialProduct.slug,
    initialData: !managedExternally && !me?.authToken ? initialProduct : undefined,
    placeholderData: managedExternally ? undefined : initialProduct,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const product = managedExternally ? initialProduct : queryProduct;

  return (
    <VariantsProvider
      key={`${product.id}-${me?.customer.id ?? 'guest'}`}
      state={{ product: product, selectedQuantity: product.min_quantity ?? 1 }}
    >
      <Suspense>
        <VariantsParamsSync />
      </Suspense>
      <DialogProvider>
        <section className={cn('custom-section', className)}>
          <div
            className={cn(
              'custom-container',
              'flex flex-col items-start justify-start gap-6 md:flex-row md:items-stretch md:justify-between lg:gap-8',
            )}
          >
            <div className="w-full md:max-w-[720px]">
              <CarouselProvider>
                <ProductImages />
              </CarouselProvider>
            </div>
            <WeekProductInfo product={product} label={data.name} />
          </div>
        </section>
      </DialogProvider>
    </VariantsProvider>
  );
}
